import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ASSETS } from '../assetManifest.js';
import { CONSTANTS } from '../data.js';
import { iconFor, getCardStats } from '../lib/utils';
import type { Instance, Effect, PaletteDrag, DragState, CardInstance, TutorialState } from '../types';
import { ElementInstance } from './ElementInstance';

type BoardProps = {
  boardRef: React.RefObject<HTMLDivElement>;
  instances: Instance[];
  effects: Effect[];
  paletteDrag: PaletteDrag | null;
  highlightedIds: number[];
  dragState: DragState;
  currentEraKey: string;
  currentEraName: string;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: () => void;
  onPointerDownInstance: (e: React.PointerEvent, id: number) => void;
  onBoardDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onBoardDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onBoardDragLeave: () => void;
  onEffectAnimationComplete: (effect: Effect) => void;
  gamePhase: string;
  playerFieldCards?: (CardInstance | null)[];
  bossFieldCards?: (CardInstance | null)[];
  roundResult?: string | null;
  currentBoss?: any;
  onStartBossBattle?: () => void;
  onNextRound?: () => void;
  onBossBattleClose?: () => void;
  dialogueIndex?: number;
  onNextDialogue?: () => void;
  onPlayCardOnField: (handCardIndex: number, fieldSlotIndex: number) => void;
  onCombineOnField: (handCardIndex: number, fieldSlotIndex: number) => void;
  playerHand?: CardInstance[];
  bossHand?: CardInstance[];
  playerAvatar: string;
  onRetryBattle?: () => void;
  onReturnToMainMenuFromDefeat?: () => void;
  onOpenDeckBuilderFromDefeat?: () => void;
  unsuccessfulCombos: number;
  onGetHint: () => void;
  deckBuilderUnlocked: boolean;
  onOpenDeckBuilder: () => void;
  tutorialState: TutorialState;
};

export const Board: React.FC<BoardProps> = ({
  boardRef,
  instances,
  effects,
  paletteDrag,
  highlightedIds,
  dragState,
  currentEraKey,
  currentEraName,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onPointerDownInstance,
  onBoardDragOver,
  onBoardDrop,
  onBoardDragLeave,
  onEffectAnimationComplete,
  gamePhase,
  playerFieldCards = [],
  bossFieldCards = [],
  roundResult,
  currentBoss,
  onStartBossBattle,
  onNextRound,
  onBossBattleClose,
  dialogueIndex = 0,
  onNextDialogue,
  onPlayCardOnField,
  onCombineOnField,
  playerHand = [],
  bossHand = [],
  playerAvatar,
  onRetryBattle,
  onReturnToMainMenuFromDefeat,
  onOpenDeckBuilderFromDefeat,
  unsuccessfulCombos,
  onGetHint,
  deckBuilderUnlocked,
  onOpenDeckBuilder,
  tutorialState,
}) => {
  const backgroundAsset = ASSETS.backgrounds?.[currentEraKey];
  const isBossBattle = gamePhase.startsWith('boss');
  const [dragOverSlot, setDragOverSlot] = useState<number | null>(null);

  // Get player name from avatar
  const playerAvatarData = CONSTANTS.player.avatars.find(avatar => avatar.key === playerAvatar);
  const playerName = playerAvatarData?.name || CONSTANTS.player.name;

  const calculateFieldStrength = (cards: (CardInstance | null)[]) => {
    return cards.filter(Boolean).reduce((sum, card) => sum + getCardStats(card!, currentEraKey).power, 0);
  };

  const renderPlayerPanel = () => {
    const playerStrength = calculateFieldStrength(playerFieldCards);
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="absolute bottom-4 right-4 flex items-center gap-4 z-10 p-4 rounded-lg"
        style={{
          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface}90 0%, ${CONSTANTS.ui.theme.colors.background}90 100%)`,
          border: `2px solid ${CONSTANTS.ui.theme.colors.success}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          backdropFilter: 'blur(8px)'
        }}
      >
        <div className="text-right">
          <h3 className="font-cinzel text-xl font-bold mb-1" style={{color: CONSTANTS.ui.theme.colors.success, textShadow: '0 1px 3px rgba(0,0,0,0.7)'}}>
            {playerName}
          </h3>
          <div className="font-crimson text-sm" style={{color: CONSTANTS.ui.theme.colors.textSecondary}}>
            Cards: {playerHand.length}
          </div>
          <div className="font-crimson text-sm" style={{color: CONSTANTS.ui.theme.colors.textSecondary}}>
            Field Strength: <span className="font-bold" style={{color: CONSTANTS.ui.theme.colors.success}}>{playerStrength}</span>
          </div>
        </div>
        <img 
          src={iconFor(playerAvatar)} 
          alt="Player" 
          className="w-16 h-16 rounded-full border-2 bg-black/50" 
          style={{borderColor: CONSTANTS.ui.theme.colors.success}} 
        />
      </motion.div>
    );
  };

  const renderBossPanel = () => {
    if (!currentBoss) return null;
    
    const bossStrength = calculateFieldStrength(bossFieldCards);
    
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="absolute top-4 left-4 flex items-center gap-4 z-10 p-4 rounded-lg"
        style={{
          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface}90 0%, ${CONSTANTS.ui.theme.colors.background}90 100%)`,
          border: `2px solid ${CONSTANTS.ui.theme.colors.error}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          backdropFilter: 'blur(8px)'
        }}
      >
        {ASSETS.bosses?.[currentBoss.key] && (
          <img 
            src={ASSETS.bosses[currentBoss.key].path} 
            alt={currentBoss.name} 
            className="w-16 h-16 rounded-full border-2 bg-black/50" 
            style={{borderColor: CONSTANTS.ui.theme.colors.error}} 
          />
        )}
        <div>
          <h3 className="font-cinzel text-xl font-bold mb-1" style={{color: CONSTANTS.ui.theme.colors.error, textShadow: '0 1px 3px rgba(0,0,0,0.7)'}}>
            {currentBoss.name}
          </h3>
          <div className="font-crimson text-sm" style={{color: CONSTANTS.ui.theme.colors.textSecondary}}>
            Cards: {bossHand.length}
          </div>
          <div className="font-crimson text-sm" style={{color: CONSTANTS.ui.theme.colors.textSecondary}}>
            Field Strength: <span className="font-bold" style={{color: CONSTANTS.ui.theme.colors.error}}>{bossStrength}</span>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderFieldCards = (cards: (CardInstance | null)[], isPlayer: boolean) => {
    const cardSlots = Array.from({ length: CONSTANTS.cardGame.fieldSlots });
  
    const handleDrop = (e: React.DragEvent, slotIndex: number) => {
      e.preventDefault();
      setDragOverSlot(null);
      if (!isPlayer) return;
      
      const handCardIndexStr = e.dataTransfer.getData('text/plain');
      if (!handCardIndexStr) return;
      
      const handCardIndex = parseInt(handCardIndexStr, 10);
      if (isNaN(handCardIndex)) return;

      const fieldCard = cards[slotIndex];
      if (fieldCard) {
        onCombineOnField(handCardIndex, slotIndex);
      } else {
        onPlayCardOnField(handCardIndex, slotIndex);
      }
    };

    const handleDragOver = (e: React.DragEvent, slotIndex: number) => {
      e.preventDefault();
      if (isPlayer) {
        setDragOverSlot(slotIndex);
        e.dataTransfer.dropEffect = 'move';
      }
    };

    return (
      <div className="grid grid-cols-3 grid-rows-2 gap-4" data-tutorial-target={isPlayer ? 'field_slots' : undefined}>
        {cardSlots.map((_, index) => {
          const card = cards[index];
          const isFuture = card?.isFuture;
          const isHovered = dragOverSlot === index;

          if (card) {
            const element = CONSTANTS.elements[card.elementKey];
            const categoryKey = element.category || 'materials';
            const categoryInfo = CONSTANTS.ui.library.categories.find(c => c.key === categoryKey);
            const frameAssetKey = categoryInfo?.frameAsset || 'default';
            const framePath = ASSETS.card_frames?.[frameAssetKey]?.path;
            const categoryColor = categoryInfo?.color || CONSTANTS.ui.theme.colors.border;

            return (
              <motion.div
                key={card.id}
                layoutId={`field-${card.id}`}
                className="relative w-28 h-40 rounded-lg p-2 transition-all duration-200 bg-cover bg-center"
                style={{
                  backgroundImage: framePath ? `url(${framePath})` : `linear-gradient(145deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
                  border: `2px solid ${isFuture ? '#00ffff' : categoryColor}`,
                  boxShadow: isFuture ? '0 0 15px #00ffff, 0 0 5px #00ffff' : `0 4px 12px rgba(0,0,0,0.4), 0 0 8px ${categoryColor}50`,
                  transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                }}
                onDrop={(e) => handleDrop(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={() => setDragOverSlot(null)}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
                data-tutorial-target={isPlayer ? 'combine_on_field' : undefined}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="font-cinzel text-[10px] font-bold text-white/90 truncate">{element.name}</span>
                  <span className="font-bold text-base" style={{color: CONSTANTS.ui.theme.colors.primary}}>{getCardStats(card, currentEraKey).power}</span>
                </div>
                <div className="w-full h-16 bg-black/20 rounded-md mb-1 flex items-center justify-center overflow-hidden">
                  <img src={iconFor(card.elementKey)} alt={element.name} className="w-14 h-14 object-contain" />
                </div>
                {isFuture && <div className="absolute bottom-1 left-2 font-cinzel text-[8px]" style={{color: '#00ffff', textShadow: '0 0 5px #00ffff'}}>Future Time</div>}
              </motion.div>
            );
          } else {
            return (
              <div 
                key={`placeholder-${index}`}
                className="w-28 h-40 rounded-lg border-2 border-dashed flex items-center justify-center transition-all duration-200"
                style={{ 
                  borderColor: isHovered ? CONSTANTS.ui.theme.colors.primary : CONSTANTS.ui.theme.colors.border,
                  backgroundColor: isHovered ? 'rgba(218, 165, 32, 0.1)' : 'transparent',
                }}
                onDrop={(e) => handleDrop(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={() => setDragOverSlot(null)}
              >
                <span 
                  className="font-crimson text-sm"
                  style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
                >
                  {isPlayer ? 'Card Slot' : 'Boss Slot'}
                </span>
              </div>
            );
          }
        })}
      </div>
    );
  };

  return (
    <div className="flex-1 relative">
      <div
        ref={boardRef}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onDragOver={onBoardDragOver}
        onDrop={onBoardDrop}
        onDragLeave={onBoardDragLeave}
        className="w-full h-full relative overflow-hidden"
        style={{
          backgroundImage: backgroundAsset?.path ? `url(${backgroundAsset.path})` : 'linear-gradient(135deg, #0f172a 0%, #111827 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/30 via-black/10 to-black/40" />

        {!isBossBattle && (
          <>
            <div className="absolute top-2 left-3 z-10 px-3 py-1 rounded bg-black/40 border border-white/10 backdrop-blur text-xs">
              Current Era: {currentEraName}
            </div>

            <div className="absolute top-2 right-3 z-10 text-xs text-white/70">
              Drag elements together to combine
            </div>

            {deckBuilderUnlocked && (
              <motion.button
                onClick={onOpenDeckBuilder}
                className="absolute bottom-4 left-4 z-20 w-20 h-20 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
                style={{
                  background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, #3d3426 100%)`,
                  border: `2px solid ${CONSTANTS.ui.theme.colors.border}`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                }}
                title="Open Deck Builder"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                data-tutorial-target="deck_builder_button"
              >
                <img src={iconFor('deck')} alt="Deck Builder" className="w-12 h-12" />
              </motion.button>
            )}

            {unsuccessfulCombos >= CONSTANTS.hints.unlockThreshold && (
              <motion.button
                onClick={onGetHint}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 w-20 h-20 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95"
                style={{
                  background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
                  border: `2px solid ${CONSTANTS.ui.theme.colors.border}`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                }}
                title="Get a hint"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
              >
                <img src={iconFor('hint')} alt="Hint" className="w-12 h-12" />
              </motion.button>
            )}

            {instances.map(inst => (
              <ElementInstance
                key={inst.id}
                inst={inst}
                isHighlighted={highlightedIds.includes(inst.id)}
                isDragging={dragState?.id === inst.id && dragState.isDragging}
                onPointerDown={onPointerDownInstance}
              />
            ))}

            {paletteDrag && paletteDrag.highlight && (
              <div
                className="absolute z-20 select-none pointer-events-none"
                style={{ left: paletteDrag.x, top: paletteDrag.y }}
              >
                <div className="w-16 h-16 rounded-lg border border-yellow-300 bg-white/5 backdrop-blur shadow-[0_0_18px_rgba(253,224,71,0.8)] scale-[1.06] transition-transform">
                  <img
                    src={iconFor(paletteDrag.key)}
                    alt={paletteDrag.key}
                    className="w-full h-full object-contain p-1.5"
                    draggable={false}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {isBossBattle && (
          <div className="w-full h-full flex flex-col items-center justify-center p-8">
            {gamePhase === 'boss_intro' && currentBoss && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-row items-stretch w-[900px] max-w-[95vw] rounded-xl overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
                  border: `2px solid ${CONSTANTS.ui.theme.colors.primary}`,
                  boxShadow: `${CONSTANTS.ui.theme.effects.ornateBoxShadow}, 0 0 30px rgba(218, 165, 32, 0.4)`
                }}
              >
                <motion.div 
                  className="w-1/3 bg-black/20 flex items-center justify-center p-4"
                  initial={{ x: -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  {ASSETS.bosses?.[currentBoss.key] && (
                    <img 
                      src={ASSETS.bosses[currentBoss.key].path} 
                      alt={currentBoss.name}
                      className="max-w-full max-h-full object-contain drop-shadow-2xl"
                    />
                  )}
                </motion.div>

                <div className="w-2/3 relative">
                  <div className="absolute top-3 left-3 w-8 h-8 border-l-2 border-t-2 rounded-tl-lg z-10" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
                  <div className="absolute top-3 right-3 w-8 h-8 border-r-2 border-t-2 rounded-tr-lg z-10" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />

                  <motion.div
                    key="initial_dialogue"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="p-8 flex flex-col justify-between h-full"
                  >
                    <div>
                      <h2 
                        className="font-cinzel text-2xl font-bold mb-2"
                        style={{ 
                          color: CONSTANTS.ui.theme.colors.primary,
                          textShadow: CONSTANTS.ui.theme.effects.textGlow
                        }}
                      >
                        {currentBoss.name}
                      </h2>
                      <p 
                        className="font-crimson text-sm mb-6"
                        style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
                      >
                        {currentBoss.description}
                      </p>

                      <div 
                        className="font-crimson text-lg leading-relaxed mb-8 p-4 rounded-lg min-h-[80px] flex items-center"
                        style={{ 
                          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary}10 0%, ${CONSTANTS.ui.theme.colors.accent}10 100%)`,
                          border: `1px solid ${CONSTANTS.ui.theme.colors.border}40`,
                          color: CONSTANTS.ui.theme.colors.text
                        }}
                      >
                        "{currentBoss.initialDialogue?.[dialogueIndex] ?? '...'}"
                      </div>
                    </div>

                    {dialogueIndex < (currentBoss.initialDialogue?.length ?? 1) - 1 ? (
                      <button
                        onClick={onNextDialogue}
                        className="font-crimson w-full px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
                        style={{
                          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
                          color: CONSTANTS.ui.theme.colors.background,
                          border: `1px solid ${CONSTANTS.ui.theme.colors.border}`,
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        Continue...
                      </button>
                    ) : (
                      <button
                        onClick={onStartBossBattle}
                        className="font-crimson w-full px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
                        style={{
                          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
                          color: CONSTANTS.ui.theme.colors.background,
                          border: `1px solid ${CONSTANTS.ui.theme.colors.border}`,
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        Begin Battle!
                      </button>
                    )}
                  </motion.div>
                </div>
              </motion.div>
            )}

            {gamePhase === 'boss_victory' && currentBoss && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-row items-stretch w-[900px] max-w-[95vw] rounded-xl overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
                  border: `2px solid ${CONSTANTS.ui.theme.colors.success}`,
                  boxShadow: `${CONSTANTS.ui.theme.effects.ornateBoxShadow}, 0 0 30px rgba(34, 139, 34, 0.4)`
                }}
              >
                <motion.div 
                  className="w-1/3 bg-black/20 flex items-center justify-center p-4"
                  initial={{ x: -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  {ASSETS.bosses?.[currentBoss.key] && (
                    <img 
                      src={ASSETS.bosses[currentBoss.key].path} 
                      alt={currentBoss.name}
                      className="max-w-full max-h-full object-contain drop-shadow-2xl filter grayscale opacity-60"
                    />
                  )}
                </motion.div>

                <div className="w-2/3 relative">
                  <div className="absolute top-3 left-3 w-8 h-8 border-l-2 border-t-2 rounded-tl-lg z-10" style={{ borderColor: CONSTANTS.ui.theme.colors.success }} />
                  <div className="absolute top-3 right-3 w-8 h-8 border-r-2 border-t-2 rounded-tr-lg z-10" style={{ borderColor: CONSTANTS.ui.theme.colors.success }} />

                  <AnimatePresence mode="wait">
                    {dialogueIndex < (currentBoss.defeatDialogue?.length ?? 0) ? (
                      <motion.div
                        key="defeat_dialogue"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="p-8 flex flex-col justify-between h-full"
                      >
                        <div>
                          <h2 
                            className="font-cinzel text-2xl font-bold mb-2"
                            style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
                          >
                            {currentBoss.name} (Defeated)
                          </h2>
                          <p 
                            className="font-crimson text-sm mb-6"
                            style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
                          >
                            The guardian acknowledges your victory...
                          </p>
                          <div 
                            className="font-crimson text-lg leading-relaxed mb-8 p-4 rounded-lg min-h-[80px] flex items-center"
                            style={{ 
                              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.success}10 0%, ${CONSTANTS.ui.theme.colors.primary}10 100%)`,
                              border: `1px solid ${CONSTANTS.ui.theme.colors.success}40`,
                              color: CONSTANTS.ui.theme.colors.text
                            }}
                          >
                            "{currentBoss.defeatDialogue?.[dialogueIndex] ?? '...'}"
                          </div>
                        </div>
                        <button
                          onClick={onNextDialogue}
                          className="font-crimson w-full px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
                          style={{
                            background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.success} 0%, #32cd32 100%)`,
                            color: CONSTANTS.ui.theme.colors.background,
                            border: `1px solid ${CONSTANTS.ui.theme.colors.success}`,
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                          }}
                        >
                          Continue...
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="victory"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="p-8 text-center flex flex-col justify-center items-center h-full"
                      >
                        <motion.h2 
                          className="font-cinzel text-4xl font-bold mb-4"
                          style={{ 
                            color: CONSTANTS.ui.theme.colors.primary,
                            textShadow: CONSTANTS.ui.theme.effects.textGlow
                          }}
                          initial={{ y: -20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.1 }}
                        >
                          Era Conquered!
                        </motion.h2>

                        <motion.div 
                          className="font-crimson text-base text-center leading-relaxed mb-8"
                          style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.3 }}
                        >
                          You have defeated the guardian and mastered this era's secrets. The path to the next age is now open!
                        </motion.div>
                        
                        <motion.button
                          onClick={onBossBattleClose}
                          className="font-crimson px-8 py-4 rounded-lg text-xl font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
                          style={{
                            background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.success} 0%, #32cd32 100%)`,
                            color: CONSTANTS.ui.theme.colors.background,
                            border: `1px solid ${CONSTANTS.ui.theme.colors.success}`,
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                          }}
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.4 }}
                        >
                          Advance to the Next Era
                        </motion.button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}

            {gamePhase === 'boss_defeat' && currentBoss && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-row items-stretch w-[900px] max-w-[95vw] rounded-xl overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
                  border: `2px solid ${CONSTANTS.ui.theme.colors.error}`,
                  boxShadow: `${CONSTANTS.ui.theme.effects.ornateBoxShadow}, 0 0 30px rgba(220, 20, 60, 0.4)`
                }}
              >
                <motion.div 
                  className="w-1/3 bg-black/20 flex items-center justify-center p-4"
                  initial={{ x: -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  {ASSETS.bosses?.[currentBoss.key] && (
                    <img 
                      src={ASSETS.bosses[currentBoss.key].path} 
                      alt={currentBoss.name}
                      className="max-w-full max-h-full object-contain drop-shadow-2xl"
                    />
                  )}
                </motion.div>

                <div className="w-2/3 relative">
                  <div className="absolute top-3 left-3 w-8 h-8 border-l-2 border-t-2 rounded-tl-lg z-10" style={{ borderColor: CONSTANTS.ui.theme.colors.error }} />
                  <div className="absolute top-3 right-3 w-8 h-8 border-r-2 border-t-2 rounded-tr-lg z-10" style={{ borderColor: CONSTANTS.ui.theme.colors.error }} />

                  <AnimatePresence mode="wait">
                    {dialogueIndex < (currentBoss.playerDefeatDialogue?.length ?? 0) ? (
                      <motion.div
                        key="player_defeat_dialogue"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="p-8 flex flex-col justify-between h-full"
                      >
                        <div>
                          <h2 
                            className="font-cinzel text-2xl font-bold mb-2"
                            style={{ color: CONSTANTS.ui.theme.colors.error }}
                          >
                            {currentBoss.name}
                          </h2>
                          <p 
                            className="font-crimson text-sm mb-6"
                            style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
                          >
                            The guardian mocks your failure...
                          </p>
                          <div 
                            className="font-crimson text-lg leading-relaxed mb-8 p-4 rounded-lg min-h-[80px] flex items-center"
                            style={{ 
                              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.error}10 0%, ${CONSTANTS.ui.theme.colors.primary}10 100%)`,
                              border: `1px solid ${CONSTANTS.ui.theme.colors.error}40`,
                              color: CONSTANTS.ui.theme.colors.text
                            }}
                          >
                            "{currentBoss.playerDefeatDialogue?.[dialogueIndex] ?? '...'}"
                          </div>
                        </div>
                        <button
                          onClick={onNextDialogue}
                          className="font-crimson w-full px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
                          style={{
                            background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.error} 0%, #8b0000 100%)`,
                            color: CONSTANTS.ui.theme.colors.text,
                            border: `1px solid ${CONSTANTS.ui.theme.colors.error}`,
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                          }}
                        >
                          Continue...
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="defeat_options"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="p-8 text-center flex flex-col justify-center items-center h-full"
                      >
                        <motion.h2 
                          className="font-cinzel text-4xl font-bold mb-4"
                          style={{ 
                            color: CONSTANTS.ui.theme.colors.error,
                            textShadow: '0 0 10px rgba(220, 20, 60, 0.5)'
                          }}
                          initial={{ y: -20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.1 }}
                        >
                          You Have Been Defeated
                        </motion.h2>

                        <motion.div 
                          className="font-crimson text-base text-center leading-relaxed mb-8"
                          style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.3 }}
                        >
                          Your journey through this era is not yet complete. Strengthen your knowledge and try again.
                        </motion.div>
                        
                        <div className="flex flex-col gap-3 w-full">
                          <motion.button
                            onClick={onRetryBattle}
                            className="font-crimson w-full px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
                            style={{
                              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
                              color: CONSTANTS.ui.theme.colors.background,
                              border: `1px solid ${CONSTANTS.ui.theme.colors.border}`,
                            }}
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4 }}
                          >
                            Retry Battle
                          </motion.button>
                          
                          {deckBuilderUnlocked && onOpenDeckBuilderFromDefeat && (
                            <motion.button
                              onClick={onOpenDeckBuilderFromDefeat}
                              className="font-crimson w-full px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
                              style={{
                                background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.accent} 0%, #b8860b 100%)`,
                                color: CONSTANTS.ui.theme.colors.background,
                                border: `1px solid ${CONSTANTS.ui.theme.colors.border}`,
                              }}
                              initial={{ y: 20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              transition={{ delay: 0.45 }}
                            >
                              Modify Deck
                            </motion.button>
                          )}
                          
                          <motion.button
                            onClick={onReturnToMainMenuFromDefeat}
                            className="font-crimson w-full px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
                            style={{
                              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, #3d3426 100%)`,
                              color: CONSTANTS.ui.theme.colors.text,
                              border: `1px solid ${CONSTANTS.ui.theme.colors.border}`,
                            }}
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5 }}
                          >
                            Return to Main Menu
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}

            {(gamePhase === 'boss_playing' || gamePhase === 'boss_round_end') && (
              <div className="w-full h-full relative flex flex-col items-center justify-between py-4">
                {renderBossPanel()}
                {renderPlayerPanel()}

                {renderFieldCards(bossFieldCards, false)}
                
                <AnimatePresence>
                  {roundResult && (
                    <motion.div 
                      initial={{scale: 2, opacity: 0}} 
                      animate={{scale: 1, opacity: 1}} 
                      exit={{opacity: 0}} 
                      className="font-cinzel text-4xl font-bold my-4"
                      style={{
                        color: roundResult === 'player' 
                          ? CONSTANTS.ui.theme.colors.success 
                          : roundResult === 'boss' 
                          ? CONSTANTS.ui.theme.colors.error 
                          : 'white',
                        textShadow: '0 2px 5px rgba(0,0,0,0.8)'
                      }}
                    >
                      {roundResult === 'player' ? 'You Win!' : roundResult === 'boss' ? 'Defeat!' : 'Tie!'}
                    </motion.div>
                  )}
                </AnimatePresence>

                {renderFieldCards(playerFieldCards, true)}

                {gamePhase === 'boss_round_end' && (
                  <button
                    onClick={onNextRound}
                    className="font-crimson absolute bottom-0 px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
                    style={{
                      background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
                      color: CONSTANTS.ui.theme.colors.background,
                    }}
                  >
                    Continue
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <AnimatePresence>
          {effects.map(effect => (
            <motion.div
              key={effect.id}
              className="absolute z-30 flex items-center justify-center pointer-events-none"
              style={{ left: effect.x, top: effect.y, width: 64, height: 64 }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [1.5, 1], opacity: [1, 0] }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              onAnimationComplete={() => onEffectAnimationComplete(effect)}
            >
              <div className="w-24 h-24 rounded-full bg-yellow-400/50 blur-lg" />
              <img src={iconFor(effect.key)} alt="" className="w-16 h-16 absolute object-contain p-1" />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};
